import {
  WaveCloudAPI,
  waveHeaderS3KeyFromCardKey,
  type WaveCoverKeys,
  type WaveRecord,
} from "@vida/wave-core";
import { createWaveApiConfig, trackActivity } from "./waveSession";

export type WaveCoverRequestOutcome = {
  userMessage?: string | null;
  coverImageS3Key?: string | null;
  coverImageHeaderS3Key?: string | null;
};

const coverUpdateListeners = new Set<(waveId: string, keys: WaveCoverKeys) => void>();

export function subscribeWaveCoverUpdates(
  listener: (waveId: string, keys: WaveCoverKeys) => void,
): () => void {
  coverUpdateListeners.add(listener);
  return () => coverUpdateListeners.delete(listener);
}

function publishCoverUpdate(waveId: string, keys: WaveCoverKeys): void {
  for (const listener of coverUpdateListeners) listener(waveId, keys);
}

export function needsWaveCover(coverImageS3Key?: string | null): boolean {
  return !coverImageS3Key?.trim();
}

export function needsAnyWaveCover(wave: WaveRecord): boolean {
  return needsWaveCover(wave.coverImageS3Key) || needsWaveCover(wave.coverImageHeaderS3Key);
}

export function hasCompleteWaveCoverSet(wave: WaveRecord): boolean {
  return !needsWaveCover(wave.coverImageS3Key) && !needsWaveCover(wave.coverImageHeaderS3Key);
}

export async function requestWaveCover(
  wave: WaveRecord,
  force = false,
  onCoverUpdated?: (keys: WaveCoverKeys) => void | Promise<void>,
): Promise<WaveCoverRequestOutcome> {
  const cfg = createWaveApiConfig();
  const priorCard = wave.coverImageS3Key?.trim() || null;
  const priorHeader = wave.coverImageHeaderS3Key?.trim() || null;
  const requestStarted = Date.now();

  trackActivity("Cover AI", force ? `Rigenera cover · ${wave.title}` : `Genera cover · ${wave.title}`, "cover");

  try {
    const result = await WaveCloudAPI.requestWaveCover(cfg, wave.waveId, force);

    const immediateKeys = pickCoverKeys(result);
    if (immediateKeys.coverImageS3Key || immediateKeys.coverImageHeaderS3Key) {
      const backfilled = result.reason === "header_backfilled";
      await deliverCover(wave.waveId, immediateKeys, force || backfilled, onCoverUpdated);
      return immediateKeys;
    }

    if (result.skipped || result.cached) {
      const refreshed = await WaveCloudAPI.getWave(cfg, wave.waveId);
      const keys = {
        coverImageS3Key: refreshed.coverImageS3Key ?? null,
        coverImageHeaderS3Key: refreshed.coverImageHeaderS3Key ?? null,
      };
      if (keys.coverImageS3Key || keys.coverImageHeaderS3Key) {
        await deliverCover(wave.waveId, keys, force, onCoverUpdated);
      }
      return keys;
    }

    if (!result.ok || !result.jobId?.trim()) {
      const msg = result.reason ?? "Impossibile richiedere la cover.";
      trackActivity("Cover AI", msg, "cover");
      console.warn("[wave cover]", wave.waveId, msg);
      return { userMessage: msg, coverImageS3Key: null, coverImageHeaderS3Key: null };
    }

    trackActivity("Cover AI", `Job ${result.jobId} — polling…`, "cover");
    const keys = await pollForCover(
      cfg,
      wave.waveId,
      result.jobId,
      force,
      priorCard,
      priorHeader,
      requestStarted,
      onCoverUpdated,
    );
    return keys;
  } catch (e) {
    const msg = e instanceof Error ? e.message : "Errore cover";
    trackActivity("Cover AI", msg, "cover");
    console.warn("[wave cover]", wave.waveId, msg);
    return { userMessage: msg, coverImageS3Key: null, coverImageHeaderS3Key: null };
  }
}

function normalizeCoverKeys(keys: WaveCoverKeys): WaveCoverKeys {
  const coverImageS3Key = keys.coverImageS3Key?.trim() || null;
  const explicitHeader = keys.coverImageHeaderS3Key?.trim() || null;
  const coverImageHeaderS3Key =
    explicitHeader || waveHeaderS3KeyFromCardKey(coverImageS3Key);
  return { coverImageS3Key, coverImageHeaderS3Key };
}

function pickCoverKeys(result: {
  coverImageS3Key?: string | null;
  coverImageHeaderS3Key?: string | null;
}): WaveCoverKeys {
  return normalizeCoverKeys({
    coverImageS3Key: result.coverImageS3Key?.trim() || null,
    coverImageHeaderS3Key: result.coverImageHeaderS3Key?.trim() || null,
  });
}

async function deliverCover(
  waveId: string,
  keys: WaveCoverKeys,
  force: boolean,
  onCoverUpdated?: (keys: WaveCoverKeys) => void | Promise<void>,
): Promise<void> {
  const normalized = normalizeCoverKeys(keys);
  await onCoverUpdated?.(normalized);
  publishCoverUpdate(waveId, normalized);
  trackActivity("Cover AI", force ? "Cover rigenerata" : "Cover pronta", "cover");
}

async function pollForCover(
  cfg: ReturnType<typeof createWaveApiConfig>,
  waveId: string,
  jobId: string,
  force: boolean,
  priorCard: string | null,
  priorHeader: string | null,
  requestStarted: number,
  onCoverUpdated?: (keys: WaveCoverKeys) => void | Promise<void>,
): Promise<WaveCoverKeys> {
  const hadPriorCard = Boolean(priorCard);
  const hadPriorHeader = Boolean(priorHeader);
  const acceptUpdatedAfter = requestStarted - 3000;
  const maxAttempts = 120;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const job = await WaveCloudAPI.getAIJob(cfg, jobId).catch(() => null);
    if (job?.status === "FAILED") {
      throw new Error(job.errorMessage?.trim() || "Job cover fallito.");
    }
    const jobCompleted = job?.status === "COMPLETED";

    const snapshot = await WaveCloudAPI.getWave(cfg, waveId);
    const card = snapshot.coverImageS3Key?.trim() || null;
    const header = snapshot.coverImageHeaderS3Key?.trim() || null;
    const resolvedHeader = header || waveHeaderS3KeyFromCardKey(card);
    if (card && resolvedHeader) {
      const cardChanged = !hadPriorCard || card !== priorCard;
      const headerChanged = !hadPriorHeader || header !== priorHeader;
      const updatedAtMs = Date.parse(snapshot.updatedAt);
      const noteUpdatedAfterJob = !Number.isNaN(updatedAtMs) && updatedAtMs >= acceptUpdatedAfter;
      const ready = cardChanged || headerChanged || noteUpdatedAfterJob || jobCompleted;
      if (ready) {
        const keys = normalizeCoverKeys({
          coverImageS3Key: card,
          coverImageHeaderS3Key: resolvedHeader,
        });
        await deliverCover(waveId, keys, force, onCoverUpdated);
        return keys;
      }
    }
    if (attempt === 1 || attempt % 5 === 0) {
      trackActivity("Cover AI", `Polling ${attempt}/${maxAttempts} · ${waveId.slice(0, 8)}…`, "cover");
    }
    if (attempt === maxAttempts) break;
    await sleep(1000);
  }
  const timeoutMsg = "Timeout in attesa della cover.";
  trackActivity("Cover AI", timeoutMsg, "cover");
  throw new Error(timeoutMsg);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
